import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { Skeleton } from 'primereact/skeleton';
import { Menu } from 'primereact/menu';
import { actions as filesActions } from '@Actions/filesActions';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import * as types from '@Constants/actionTypes';
import { FILE_STATUSES } from '@Constants/constants';
import Notification from '@Common/Notification';
import { LocationTaskFile } from './Types';
import './FileCell.scss';

interface FileCellProps {
    file: LocationTaskFile;
    setIsSavingFile: React.Dispatch<React.SetStateAction<boolean>>;  // Parent component needs to know when to disable some file actions so we use it's state instead of ours
    showGallery: (fileName: string) => void;
    editFile: (fileId: number) => void;
    deleteFile: (file: LocationTaskFile) => Promise<any>;
    handleSavedFile: (file: LocationTaskFile) => void;
    isMobile: boolean;
}

const FileCell = (props: FileCellProps): ReactElement => {

    const ACTION_MENU_OPTIONS = {
        Download: 'download',
        Edit: 'edit',
        Delete: 'delete',
        Approve: 'approve',
        Pending: 'pending',
        Deny: 'deny',
    };

    const menuOptions = [
        { label: "Download", value: ACTION_MENU_OPTIONS.Download, command: () => onDownloadFile(props.file) },
        { label: "Edit", value: ACTION_MENU_OPTIONS.Edit, command: () => props.editFile(props.file.id) },
        { label: "Delete", value: ACTION_MENU_OPTIONS.Delete, command: () => props.deleteFile(props.file) }
    ];

    if (props.file.fileStatusId !== FILE_STATUSES.Approved)
        menuOptions.push({ label: "Approve", value: ACTION_MENU_OPTIONS.Approve, command: () => saveFileStatus(FILE_STATUSES.Approved) });
    if (props.file.fileStatusId !== FILE_STATUSES.Denied)
        menuOptions.push({ label: "Deny", value: ACTION_MENU_OPTIONS.Deny, command: () => saveFileStatus(FILE_STATUSES.Denied) });
    if (props.file.fileStatusId !== FILE_STATUSES.Pending)
        menuOptions.push({ label: "Pending", value: ACTION_MENU_OPTIONS.Pending, command: () => saveFileStatus(FILE_STATUSES.Pending) });

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const dispatch: Dispatch = useDispatch();
    const downloadFile = async (fileName: string, promptSave: boolean) => dispatch<any>(filesActions.download(fileName, false, promptSave));
    const replaceFile = async (request) => dispatch<any>(locationTaskActions.updateFile(request));

    const menuRef: React.MutableRefObject<Menu | null> = useRef(null);
    const menuParentRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

    useEffect(() => {
        const fetchData = async () => {

            try {
                const imageBlob = await downloadFile(props.file.fileName, false);
                props.file.imageObjectURL = URL.createObjectURL(imageBlob);
            } catch (error) {
                Notification('error', 'An Error Occurred', error);
            } finally {
                setIsLoading(false);
            }
        };

        // Only load if we have a valid file and it is an image
        if (props.file.id && getFileIcon(props.file.extension) === 'file-image') {
            fetchData();
        } else {
            setIsLoading(false);
        }

        return () => {
            // Cleanup (remove the image object URL to free memory)
            if (props.file.imageObjectURL) {
                URL.revokeObjectURL(props.file.imageObjectURL);
            }
        };
    }, []);

    const getFileIcon = (extension: string): string => {
        switch (extension.toLowerCase()) {
            case '.pdf':
                return 'file-pdf';
            case '.doc':
            case '.docx':
            case '.dotx':
                return 'file-word';
            case '.xls':
            case '.xlsx':
            case '.xlsm':
            case '.xlsb':
                return 'file-excel';
            case '.ppt':
            case '.pptx':
                return 'file-powerpoint';
            case '.png':
            case '.jpg':
            case '.jpeg':
            case '.gif':
            case '.bmp':
            case '.tif':
            case '.tiff':
            case '.jfif':
                return 'file-image';
            case '.eps':
            case '.svg':
                return 'file-vector';
            case '.zip':
            case '.rar':
            case '.7z':
                return 'file-zip';
            case '.mp3':
            case '.wav':
                return 'file-audio';
            case '.mp4':
            case '.mov':
            case '.wmv':
                return 'file-video';
            case '.txt':
            case '.csv':
            case '.log':
                return 'file-lines';
            case '.xml':
                return 'file-xml';
            case '.msg':
            case '.eml':
                return 'envelope';
            case '.htm':
            case '.html':
            case '.css':
                return 'file-code';
            case '.exe':
            case '.ndf':
            case '.dat':
                return 'file-binary';
            default:
                return 'file';
        }
    }

    const onFileClick = async (file, e) => {
        e.preventDefault();

        // We either download the file for non-image files or signal the parent to show the gallery
        if (getFileIcon(file.extension) !== 'file-image') {
            await onDownloadFile(file);
        } else {
            props.showGallery(file.name);
        }
    }

    const onDownloadFile = async (file: LocationTaskFile) => {
        try {
            props.setIsSavingFile(true);
            await downloadFile(file.fileName, true);
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            props.setIsSavingFile(false);
        }
    }

    const onActionsClick = (e) => {
        e.stopPropagation();
        menuRef.current?.toggle(e);
    }

    const saveFileStatus = async (statusId: number) => {
        const request = {
            ...props.file,
            fileStatusId: statusId,
        };

        try {
            const response = await replaceFile(request);
            if (response.type === types.UPDATE_LOCATION_TASK_FILE_SUCCESS) {
                if (response.payload && response.payload.file) {
                    if (props.file.imageObjectURL) {
                        URL.revokeObjectURL(props.file.imageObjectURL);
                        const imageBlob = await downloadFile(response.payload.file.fileName, false);
                        response.payload.file.imageObjectURL = URL.createObjectURL(imageBlob);
                    }

                    props.handleSavedFile(response.payload.file);
                    Notification('success', 'Task Updated', 'File has been saved successfully');
                }
            } else if (response.type === types.UPDATE_LOCATION_TASK_FILE_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            props.setIsSavingFile(false);
        }
    }

    return (
        <div className='file-cell' onClick={e => onFileClick(props.file, e)}>
            {isLoading
                ?
                <Skeleton width="100%" height="100%" />
                :
                <>
                    <div className='file-cell-icon'>
                        {
                            props.file.imageObjectURL
                                ? <img src={props.file.imageObjectURL} alt={props.file.name} />
                                : <i className={`far fa-${getFileIcon(props.file.extension)} file-icon`}></i>
                        }
                        {
                            props.file.fileStatusId === FILE_STATUSES.Approved
                                ?
                                <span className="status-icon-overlay" title="File Approved"><i className="fa-solid fa-circle-check" /></span>
                                : props.file.fileStatusId === FILE_STATUSES.Denied
                                    ?
                                    <span className="status-icon-overlay" title="File Denied"><i className="fa-solid fa-circle-xmark" /></span>
                                    :
                                    null
                        }
                    </div>
                    <div className='file-cell-name' title={props.file.name + props.file.extension}>
                        <span>{props.file.name + props.file.extension}</span>
                        <div className='file-cell-menu' onClick={(e) => onActionsClick(e)} ref={el => menuParentRef.current = el}>
                            <Menu model={menuOptions} popup ref={el => menuRef.current = el} autoZIndex={true} baseZIndex={40001} className="quick-menu" />
                            <i className="fa-duotone fa-regular fa-circle-ellipsis"></i>
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

export default FileCell;
